/** Coming up's behavior half: the dashboard's upcoming bookings after the next one. Draws nothing. */
import { useMemo } from 'react';
import { formatCardTime, getBookingCardPill } from '@/pages/BookingHubPage/homeFormat';

import { useSiteStudio } from '../../api/site';
import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { keepTimes } from '../../format';
import { useWebDashboard } from '../../hooks/useWebAccount';
import { usePageMover } from '../../pageMover';
import { wholeCount } from '../../wholeCount';
import { contract, type ComingUpActions, type ComingUpEvents, type ComingUpState } from './contract';

export type ComingUpRuntime = PieceRuntime<typeof contract, ComingUpState, ComingUpActions>;

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short' });
const MONTH_LONG = new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' });
const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'short' });

function isToday(iso: string): boolean {
  return new Date(iso).toDateString() === new Date().toDateString();
}

export function useComingUp(input: PieceInput<ComingUpEvents> = {}): ComingUpRuntime {
  const options = usePieceOptions(contract, input.options);
  const navigate = usePageMover();
  const d = useWebDashboard().data;
  const studioName = useSiteStudio().data?.name ?? '';
  const upcoming = d?.upcoming;
  const next = d?.nextBooking ?? null;

  const state = useMemo<ComingUpState>(() => {
    const all = upcoming ?? [];
    // Coming up follows the next session; with none booked there is nothing to follow.
    const rest = next ? all.filter((b) => b.id !== next.id).slice(0, wholeCount(options.limit)) : [];
    return {
      bookings: rest.map((b) => {
        const at = new Date(b.scheduledAt);
        return {
          id: b.id,
          month: MONTH.format(at),
          day: String(at.getDate()),
          dateLabel: MONTH_LONG.format(at),
          name: b.service.name,
          when: keepTimes(`${WEEKDAY.format(at)}, ${formatCardTime(b.scheduledAt)}${b.service.kind === 'event' ? '' : ` · ${b.duration} min`}`),
          where: b.location.isByPhone ? 'By phone' : b.location.isVirtual ? 'Online' : b.location.name || studioName,
          status: getBookingCardPill(b).label,
          href: `/account/bookings/${encodeURIComponent(b.id)}`,
        };
      }),
      total: `${all.length} upcoming ${all.length === 1 ? 'booking' : 'bookings'} in all${next && isToday(next.scheduledAt) ? ", counting today's." : '.'}`,
      allHref: '/account/bookings',
    };
  }, [upcoming, next, options.limit, studioName]);

  const actions = useMemo<ComingUpActions>(() => ({ open: (href) => navigate(href) }), [navigate]);
  return { state, actions, options };
}
